<script setup lang="ts">
import { ref, computed, useSlots } from 'vue';
import { useDisplay } from 'vuetify';
import { useMobile } from '@/composables/useMobile';

// Shared responsive header for index/list pages. Replaces the hand-rolled
// "double-toolbar" header (title + actions, then tabs + search + filters pinned
// to hardcoded v-col-2/3/4 widths that overflow phones).
//   <list-toolbar title="Profesionales" v-model:search="search"
//                 search-label="Buscar" :active-filter-count="count">
//     <template #actions> ...buttons... </template>
//     <template #tabs> <v-tabs v-model="scope">...</v-tabs> </template>
//     <template #filters> ...filter fields (no width classes)... </template>
//   </list-toolbar>
// On desktop the filters sit inline; below 960px they collapse behind a
// "Filtros" badge button that opens a temporary side drawer (mirrors calendar-v2).
const props = withDefaults(defineProps<{
  title?: string;
  searchLabel?: string;
  activeFilterCount?: number;
}>(), { title: undefined, searchLabel: undefined, activeFilterCount: 0 });

const search = defineModel<string>('search', { default: '' });

const slots = useSlots();
const { isMobile, mdAndUp } = useMobile();
const { width } = useDisplay();

const filtersDrawer = ref(false);
const hasFilters = computed(() => !!slots.filters);
const hasSearch = computed(() => !!props.searchLabel);
const DRAWER_MAX_WIDTH = 360;
const drawerWidth = computed(() => Math.min(DRAWER_MAX_WIDTH, width.value));
</script>
<template>
  <v-toolbar
    v-if="title || $slots.actions"
    color="white"
    flat
    class="list-toolbar-row"
  >
    <v-toolbar-title
      v-if="title"
      class="font-weight-bold"
    >
      {{ title }}
    </v-toolbar-title>
    <v-spacer />
    <div class="d-flex align-center flex-wrap ga-2 mr-2">
      <slot name="actions" />
    </div>
  </v-toolbar>

  <v-toolbar
    v-if="$slots.tabs || hasSearch || hasFilters"
    color="white"
    flat
    class="list-toolbar-row list-toolbar-controls"
  >
    <slot name="tabs" />
    <v-spacer />
    <v-text-field
      v-if="hasSearch"
      v-model="search"
      :label="searchLabel"
      class="list-toolbar-search mx-4"
      prepend-inner-icon="mdi-magnify"
      single-line
      variant="underlined"
      hide-details
    />
    <div
      v-if="hasFilters && mdAndUp"
      class="list-toolbar-filters d-flex align-center ga-4 mr-4"
    >
      <slot name="filters" />
    </div>
    <v-btn
      v-if="hasFilters && isMobile"
      variant="text"
      class="mr-2"
      prepend-icon="mdi-filter-variant"
      @click="filtersDrawer = true"
    >
      <template #prepend>
        <v-badge
          :model-value="activeFilterCount > 0"
          :content="activeFilterCount"
          color="primary"
        >
          <v-icon icon="mdi-filter-variant" />
        </v-badge>
      </template>
      Filtros
    </v-btn>
  </v-toolbar>

  <v-navigation-drawer
    v-if="hasFilters && isMobile"
    v-model="filtersDrawer"
    temporary
    location="right"
    :width="drawerWidth"
  >
    <div class="list-toolbar-drawer d-flex flex-column ga-4 pa-4">
      <div class="d-flex align-center justify-space-between">
        <span class="text-subtitle-1 font-weight-bold">Filtros</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="filtersDrawer = false"
        />
      </div>
      <slot name="filters" />
    </div>
  </v-navigation-drawer>
</template>
<style scoped>
.list-toolbar-search {
  max-width: 280px;
}

.list-toolbar-filters :deep(.v-input) {
  min-width: 180px;
  max-width: 220px;
}

/* In the mobile drawer the same filter fields read as full-width stacked rows. */
.list-toolbar-drawer :deep(.v-input) {
  width: 100%;
  max-width: 100%;
}

/* Phones only: let each toolbar row flow onto multiple lines and give the
   search the full width. Gated at 600px so desktop/tablet stays single-row.
   `height: auto !important` is required because Vuetify pins .v-toolbar__content
   to --v-toolbar-height (64px), which would clip the wrapped rows. */
@media (max-width: 600px) {
  .list-toolbar-row.v-toolbar {
    height: auto !important;
  }

  .list-toolbar-row :deep(.v-toolbar__content) {
    height: auto !important;
    flex-wrap: wrap;
    row-gap: 8px;
    padding-block: 8px;
  }

  /* Title takes its own row so it isn't ellipsised by the action buttons,
     which then wrap to the next line. */

  .list-toolbar-row :deep(.v-toolbar-title) {
    flex: 1 0 100%;
    overflow: visible;
  }

  .list-toolbar-search {
    max-width: 100%;
    flex: 1 1 100%;
  }
}
</style>
